<template>
  <div class="sld" :class="{ 'sld--disabled': disabled }" :data-testid="testId || undefined">
    <div class="sld-piste">
      <div class="sld-rail"></div>
      <!-- Le PLANCHER : sous l'ancienne valeur, grisé et hors de portée ; l'ancienne
           valeur elle-même reste atteignable, marquée d'un repère. -->
      <template v-if="plancher != null && !double">
        <div class="sld-acquis" :style="styleAcquis" data-testid="slider-acquis"></div>
        <div class="sld-repere" :style="styleRepere" data-testid="slider-repere"></div>
      </template>
      <div class="sld-remplissage" :style="styleRemplissage"></div>

      <input
        v-for="(valeur, index) in poignees"
        :key="index"
        type="range"
        class="sld-poignee"
        :class="{ 'sld-poignee--haute': index === 1 }"
        :min="min"
        :max="max"
        :step="step"
        :value="valeur"
        :disabled="disabled"
        :aria-label="libelleAria(index)"
        :aria-valuetext="texteDeValeur(valeur)"
        :data-testid="testId ? `${testId}-poignee-${index}` : undefined"
        @input="deplacer(index, Number($event.target.value), $event.target)"
      >
    </div>

  </div>
</template>

<script setup>
/**
 * Un curseur — une poignée, ou deux.
 *
 * POURQUOI UN SEUL COMPOSANT pour les deux. Une fourchette de prix et un rayon
 * kilométrique partagent tout ce qui est difficile : la piste, le remplissage
 * qui suit les poignées, la mise au pas (`step`), l'accessibilité au clavier et
 * la valeur lisible sous le contrôle. Seul le nombre de poignées diffère, et il
 * se déduit de la valeur : un nombre pour une poignée, une paire pour deux. En
 * écrire deux composants, c'était accepter que la couleur du remplissage de
 * l'un dérive un jour de celle de l'autre.
 *
 * LA VALEUR NE S'ÉCRIT PAS ICI. Elle vivait sous la piste, et la hauteur du
 * curseur cessait alors d'égaler celle des champs voisins : dans une rangée de
 * filtres alignée par le bas, il dépassait d'une ligne. C'est l'appelant qui
 * dit la valeur, là où elle se lit le mieux — dans l'étiquette du champ, que
 * l'œil parcourt déjà. Le curseur, lui, occupe exactement la hauteur d'un
 * contrôle de liste.
 *
 * DES `<input type="range">` NATIFS, empilés, et non des poignées maison :
 * c'est ce qui donne gratuitement le clavier (flèches, Origine/Fin), le focus
 * visible, le rôle de contrôle pour les lecteurs d'écran et le comportement
 * tactile. La piste native est masquée, seule la poignée reste cliquable ;
 * la piste visible et son remplissage sont deux blocs posés dessous.
 *
 * LES DEUX POIGNÉES NE SE CROISENT PAS : elles se poussent. Tirer la basse
 * au-delà de la haute la bloque à hauteur de sa voisine, plutôt que d'inverser
 * les deux bornes dans le dos de celui qui tire — une fourchette qui
 * s'inverserait ne montrerait plus rien du tout.
 */
import { computed } from 'vue'

const props = defineProps({
  /** Un nombre (une poignée) ou une paire `[bas, haut]` (deux poignées). */
  modelValue: { type: [Number, Array], default: 0 },
  min: { type: Number, default: 0 },
  max: { type: Number, default: 100 },
  step: { type: Number, default: 1 },
  disabled: { type: Boolean, default: false },
  /**
   * Comment une valeur s'ÉNONCE (« 80 km », « 750 € »). Elle ne s'écrit pas
   * sous la piste — c'est l'appelant qui la porte, dans l'étiquette du champ —
   * mais elle est dite aux lecteurs d'écran par `aria-valuetext` : « 750 »
   * tout seul ne dit pas des euros.
   */
  format: { type: Function, default: (v) => String(v) },
  ariaLabel: { type: String, default: '' },
  testId: { type: String, default: '' },
  /**
   * Une poignée seulement : la valeur déjà acquise (l'ancien rayon d'une zone
   * qu'on élargit). Ce qui est en dessous se grise et la poignée n'y descend
   * pas ; la valeur elle-même reste atteignable, marquée d'un repère.
   */
  plancher: { type: Number, default: null },
})

const emit = defineEmits(['update:modelValue'])

const double = computed(() => Array.isArray(props.modelValue))

const poignees = computed(() => (double.value ? props.modelValue : [props.modelValue]))

/** Position d'une valeur sur la piste, en pourcentage. */
function pourcentage(valeur) {
  const etendue = props.max - props.min
  if (etendue <= 0) return 0
  return ((Math.min(props.max, Math.max(props.min, valeur)) - props.min) / etendue) * 100
}

/** La plus petite valeur qu'on peut choisir : le plancher lui-même. */
const premierChoix = computed(() => (props.plancher == null ? props.min : Math.min(props.max, props.plancher)))

/** Le lecteur d'écran entend que la poignée est au plancher : elle ne descendra pas plus bas. */
const texteDeValeur = (valeur) => (
  props.plancher != null && !double.value && valeur <= props.plancher ? `${props.format(valeur)}, minimum` : props.format(valeur)
)

const styleAcquis = computed(() => ({
  left: '8px',
  width: `calc((100% - 16px) * ${pourcentage(props.plancher) / 100})`,
}))

const styleRepere = computed(() => ({ left: `calc(8px + (100% - 16px) * ${pourcentage(props.plancher) / 100} - 1px)` }))

/**
 * Le remplissage se pose DANS la piste, qui garde 8 px de marge de chaque côté :
 * c'est le demi-diamètre d'une poignée. Sans cette marge, une poignée poussée à
 * fond débordait du cadre du champ.
 */
const styleRemplissage = computed(() => {
  const [bas, haut] = double.value
    ? props.modelValue
    : [props.plancher != null ? props.plancher : props.min, props.modelValue]
  const depart = pourcentage(bas)
  return {
    left: `calc(8px + (100% - 16px) * ${depart / 100})`,
    width: `calc((100% - 16px) * ${Math.max(0, pourcentage(haut) - depart) / 100})`,
  }
})

function libelleAria(index) {
  if (!double.value) return props.ariaLabel || undefined
  const role = index === 0 ? 'minimum' : 'maximum'
  return props.ariaLabel ? `${props.ariaLabel} — ${role}` : role
}

function deplacer(index, valeur, champ) {
  if (!double.value) {
    // Sous le plancher, la poignée revient au premier choix possible (le champ
    // natif aussi : sans cela il resterait dessiné là où on l'a lâché).
    const retenue = props.plancher != null ? Math.max(valeur, premierChoix.value) : valeur
    if (champ && retenue !== valeur) champ.value = String(retenue)
    emit('update:modelValue', retenue)
    return
  }

  const [bas, haut] = props.modelValue
  emit('update:modelValue', index === 0 ? [Math.min(valeur, haut), haut] : [bas, Math.max(valeur, bas)])
}
</script>

<style scoped>
.sld {
  width: 100%;
}

/* La piste occupe la hauteur d'un champ de la carte de filtres : c'est ce qui
   fait qu'un curseur posé dans une rangée de filtres s'aligne avec ses voisins
   au lieu de flotter au-dessus ou de dépasser en dessous. */
.sld-piste {
  position: relative;
  min-height: var(--list-control-h, 44px);
  display: flex;
  align-items: center;
  padding: 0 8px;
}

.sld-rail,
.sld-remplissage {
  position: absolute;
  height: 4px;
  border-radius: 9999px;
  pointer-events: none;
}

.sld-rail {
  left: 8px;
  right: 8px;
  background: var(--color-border);
}

.sld-remplissage {
  background: var(--color-primary);
}

/* Le repère de l'ancienne valeur : un trait vertical, au-dessus de la piste. */
.sld-repere {
  position: absolute;
  width: 2px;
  height: 18px;
  border-radius: 1px;
  background: var(--color-text-secondary);
  pointer-events: none;
}

/* L'acquis : plus sombre que le rail, rayé, pour dire « déjà couvert, hors de portée ». */
.sld-acquis {
  position: absolute;
  height: 8px;
  border-radius: 9999px;
  pointer-events: none;
  background: repeating-linear-gradient(
    45deg,
    var(--color-text-tertiary) 0 3px,
    var(--color-border) 3px 6px
  );
  opacity: 0.6;
}

/* La piste native disparaît : seule la poignée reste saisissable, pour que les
   deux <input> empilés ne se volent pas le clic. */
.sld-poignee {
  position: absolute;
  left: 8px;
  right: 8px;
  width: calc(100% - 16px);
  margin: 0;
  height: 20px;
  background: none;
  appearance: none;
  -webkit-appearance: none;
  pointer-events: none;
}

.sld-poignee:focus {
  outline: none;
}

/* La poignée haute passe DEVANT la basse. Les deux <input> se recouvrent
   exactement ; quand les deux valeurs coïncident, celle du dessus est la seule
   saisissable — si c'était la basse, une fourchette refermée à fond ne pourrait
   plus jamais se rouvrir par la droite. */
.sld-poignee--haute {
  z-index: 1;
}

.sld-poignee::-webkit-slider-thumb {
  appearance: none;
  -webkit-appearance: none;
  pointer-events: auto;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--color-bg-primary);
  border: 3px solid var(--color-primary);
  box-shadow: var(--shadow-sm);
  cursor: grab;
}

.sld-poignee::-moz-range-thumb {
  pointer-events: auto;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--color-bg-primary);
  border: 3px solid var(--color-primary);
  box-shadow: var(--shadow-sm);
  cursor: grab;
}

.sld-poignee:focus-visible::-webkit-slider-thumb {
  box-shadow: 0 0 0 3px var(--color-primary-light, rgba(59, 130, 246, 0.35));
}

.sld-poignee:focus-visible::-moz-range-thumb {
  box-shadow: 0 0 0 3px var(--color-primary-light, rgba(59, 130, 246, 0.35));
}

.sld--disabled .sld-remplissage {
  background: var(--color-border);
}

.sld--disabled .sld-poignee::-webkit-slider-thumb {
  border-color: var(--color-border);
  cursor: not-allowed;
}

.sld--disabled .sld-poignee::-moz-range-thumb {
  border-color: var(--color-border);
  cursor: not-allowed;
}

</style>
